// src/modules/goldVault/pages/InventoryTimeline.jsx
// Full lifecycle timeline for a single gold case.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Timeline, Card, Descriptions, Tag, Typography, Spin, Button, Space, Row, Col,
} from 'antd';
import { ArrowLeftOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getCaseTimeline } from '../api';

const { Title, Text } = Typography;

const EVENT_COLOR = {
  CUSTOMER_CREATED: 'blue', GOLD_EVALUATED: 'blue',
  SETTLEMENT_PAID: 'green', BILL_GENERATED: 'green',
  ADDED_TO_BRANCH_VAULT: 'gold', TRANSFER_CREATED: 'cyan',
  TRANSFER_APPROVED: 'cyan', TRANSFER_PACKED: 'cyan',
  TRANSFER_DISPATCHED: 'orange', RECEIVED_BY_HO: 'purple',
  HO_VERIFIED: 'purple', ADDED_TO_BATCH: 'lime',
  DISPATCHED_TO_SMELTER: 'magenta', RECEIPT_CONFIRMED: 'green',
  PROFIT_RECORDED: 'green', ADJUSTMENT: 'orange',
};

const fmtW = (v) => v != null ? `${Number(v).toFixed(3)}g` : null;

export default function InventoryTimeline() {
  const { caseId, caseType } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (caseId && caseType) load(); }, [caseId, caseType]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCaseTimeline(caseType, caseId);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Spin style={{ display: 'block', padding: 48 }} />;

  const c = data?.case;
  const timeline = data?.timeline || [];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
            <Title level={4} style={{ margin: 0 }}>
              Inventory Timeline — {c?.caseId || caseId}
            </Title>
          </Space>
        </Col>
      </Row>

      {c && (
        <Card title="Case Details" size="small" style={{ marginBottom: 20 }}>
          <Descriptions size="small" column={3} bordered>
            <Descriptions.Item label="Case No.">{c.caseId}</Descriptions.Item>
            <Descriptions.Item label="Customer">{c.customerId?.name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{c.customerId?.phone || '—'}</Descriptions.Item>
            <Descriptions.Item label="Branch">{c.branch?.name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status">{c.status}</Descriptions.Item>
            <Descriptions.Item label="Vault Status">
              <Tag color="purple">{c.inventoryStatus || 'PENDING'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Gross Wt">{fmtW(c.totalGrossWeight)}</Descriptions.Item>
            <Descriptions.Item label="Net Wt">{fmtW(c.totalNetWeight)}</Descriptions.Item>
            {c.inventoryTransferId && (
              <Descriptions.Item label="Transfer">
                <Button type="link" size="small" onClick={() => navigate(`/vault/transfers/${c.inventoryTransferId._id}`)}>
                  {c.inventoryTransferId.transferNumber}
                </Button>
              </Descriptions.Item>
            )}
            {c.inventoryBatchId && (
              <Descriptions.Item label="Batch">
                <Button type="link" size="small" onClick={() => navigate(`/gold-inventory/${c.inventoryBatchId._id}`)}>
                  {c.inventoryBatchId.batchId}
                </Button>
              </Descriptions.Item>
            )}
            {c.smelterDispatchId && (
              <Descriptions.Item label="Dispatch">
                <Button type="link" size="small" onClick={() => navigate(`/smelters/dispatches/${c.smelterDispatchId._id}`)}>
                  {c.smelterDispatchId.dispatchId}
                </Button>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      <Card title="Lifecycle Timeline" size="small">
        {timeline.length === 0 ? (
          <Text type="secondary">No timeline events recorded yet.</Text>
        ) : (
          <Timeline
            mode="left"
            items={timeline.map((event) => ({
              dot: <ClockCircleOutlined style={{ fontSize: 14, color: EVENT_COLOR[event.event] || '#1677ff' }} />,
              label: dayjs(event.createdAt).format('DD MMM YYYY HH:mm'),
              children: (
                <div>
                  <div>
                    <Tag color={EVENT_COLOR[event.event] || 'blue'}>{event.event.replace(/_/g, ' ')}</Tag>
                    {event.performedBy?.name && <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>by {event.performedBy.name}</Text>}
                  </div>
                  {event.description && <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{event.description}</div>}
                  {event.referenceNumber && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      Ref: {event.referenceModel} — {event.referenceNumber}
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Card>
    </div>
  );
}
